import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useFechar } from '../input/useAtalhos';
import { usePrenderFoco } from '../input/usePrenderFoco';
import { cursorArraste } from '../input/cursor';
import { useCompacto, useModo } from './hud/useModoTela';
import { aoRestaurar, gravarJanela, lerJanela } from './hud/posicoes';
import { t } from '../i18n/idioma';

/**
 * Onde a janela nasce. Ancorar em um canto (em vez de x/y absolutos) faz o
 * arranjo se comportar em qualquer tela: quatro painéis, quatro cantos.
 * Depois do primeiro arrasto quem manda é o jogador.
 */
export interface WindowAnchor {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

function applyAnchor(el: HTMLElement, anchor: WindowAnchor) {
  el.style.left = anchor.left !== undefined ? `${anchor.left}px` : '';
  el.style.right = anchor.right !== undefined && anchor.left === undefined ? `${anchor.right}px` : '';
  el.style.top = anchor.top !== undefined ? `${anchor.top}px` : '';
  el.style.bottom = anchor.bottom !== undefined && anchor.top === undefined ? `${anchor.bottom}px` : '';
}

interface GumpWindowProps {
  title: string;
  anchor: WindowAnchor;
  wide?: boolean;
  className?: string;
  /** Reaplica a âncora sempre que `shown` volta a true (fecha e abre no mesmo canto). */
  snapAnchor?: boolean;
  /** Aplica a âncora na primeira vez que `shown` fica true. Depois o arrasto manda. */
  placeOnShow?: boolean;
  /** Reaplica a âncora sempre que ela muda. A aposta usa isso para ficar ao lado da mochila. */
  seguirAncora?: boolean;
  /** Abre no meio da tela. Depois do arrasto, vale a posição do jogador até fechar. */
  center?: boolean;
  shown?: boolean;
  resizable?: boolean;
  /** Sem cabeçalho de madeira: a arte do filho é o gump. Arrasta o painel inteiro. */
  bare?: boolean;
  /** Arrasta pelo corpo também — o gump de grupo não tem alça além do título. */
  dragAnywhere?: boolean;
  /** Aceito pelas janelas; F1–F6 e Espaço da barra passam mesmo assim. */
  comeSlots?: boolean;
  /** `false`: o Esc não fecha (o minimapa só abre e fecha pela tecla dele, Renato 03/11). O ✕ continua. */
  fechaNoEsc?: boolean;
  /** Ação colada ao título, à esquerda — sem aumentar a barra. */
  headLead?: ReactNode;
  /** Abas ou ações à direita do título, antes do fechar. */
  headExtra?: ReactNode;
  /**
   * O minimizar padrão (techspec-hud-v3 §18): um botão com moldura ao lado do ✕, para não se confundir com o
   * filete dourado do título. Quem minimiza decide o que fica no lugar (a pílula do idle, por exemplo).
   */
  onMinimize?: () => void;
  /** Modal de verdade: role="dialog", aria-modal e Tab preso na janela inteira (o ✕ incluso). */
  dialogo?: boolean;
  /** Seletor do foco inicial do diálogo, ex.: `[data-foco-inicial]`. */
  focoInicial?: string;
  /** Classe extra no .gump__body (p-chat__corpo, p-amg__corpo, p-gld__corpo…). */
  corpoClasse?: string;
  /** Janela de HUD: não vira folha no celular. Põe a classe `win--hud`. */
  hud?: boolean;
  /** Logo depois do <h2>, dentro de .gump__title-row (a contagem de Amigos). */
  tituloExtra?: ReactNode;
  /**
   * Nome com que a posição fica guardada no navegador (techspec-hud-v3 §02). Sem ele, vale o primeiro
   * `win--*` da classe. `false` não guarda. Diálogo e janela que segue a âncora nunca guardam.
   */
  posicao?: string | false;
  onClose?: () => void;
  children: ReactNode;
}

const MARGIN = 8;
const MIN_W = 280;

/*
  A janela aberta por último vai à frente (Renato, 03/11/2026), e o clique numa janela também a traz. A ordem vira a
  variável `--win-z` (10..29, abaixo das pops, 30+), lida só pela regra base `.win` (hud.css). Quem tem z-index próprio
  numa regra mais específica (pop, compra, chat, vitais, o destaque do tutorial) continua com o seu.
*/
const Z_BASE = 10;
const Z_MAX = 20;
const pilha: HTMLElement[] = [];
function trazerParaFrente(el: HTMLElement) {
  const i = pilha.indexOf(el);
  if (i >= 0) pilha.splice(i, 1);
  for (let k = pilha.length - 1; k >= 0; k--) if (!pilha[k].isConnected) pilha.splice(k, 1);
  pilha.push(el);
  if (pilha.length > Z_MAX) pilha.splice(0, pilha.length - Z_MAX);
  pilha.forEach((w, k) => { w.style.setProperty('--win-z', String(Z_BASE + k)); });
}
function largarDaPilha(el: HTMLElement) {
  const i = pilha.indexOf(el);
  if (i >= 0) pilha.splice(i, 1);
  el.style.removeProperty('--win-z');
}
const MIN_H = 160;

/**
 * Janela arrastável, como os gumps soltos do cliente do UO.
 *
 * O arrasto escreve a posição direto no style do elemento: passar por estado
 * do React renderizaria o conteúdo inteiro a cada pixel de movimento.
 */
/**
 * A área produtiva do jogo, nas coordenadas do `left`/`top` da janela: abaixo da barra do topo e acima da
 * barra de baixo (a skillbar), dentro do mundo. Fora dela a janela cobria o HUD e dava os bugs visuais.
 * - A barra de baixo só limita quem passa por cima dela na horizontal (o chat fica ao lado, no canto).
 * - Janela mais alta que o vão entre as barras pode descer sobre a barra de baixo, até a borda do mundo.
 * Fora do mundo (entrada, landing), vale a tela inteira.
 */
function limitesDaArea(el: HTMLElement, x: number) {
  const pai = (el.offsetParent as HTMLElement | null)?.getBoundingClientRect() ?? new DOMRect(0, 0, window.innerWidth, window.innerHeight);
  const w = el.offsetWidth, h = el.offsetHeight;
  const mundo = el.closest('.world');
  let topo = pai.top + MARGIN;
  let base = Math.min(pai.bottom, window.innerHeight) - MARGIN;
  const esq = Math.max(pai.left, 0) + MARGIN;
  const dir = Math.min(pai.right, window.innerWidth) - MARGIN;
  // `win--limitada`: HUD que também respeita o topo e a barra de baixo (as placas de vida, techspec-hud-v3 §20).
  if (mundo && (!el.classList.contains('win--hud') || el.classList.contains('win--limitada'))) {
    const barraTopo = mundo.querySelector<HTMLElement>('.topbar');
    // getClientRects: a barra pode ser `position: fixed` (sem offsetParent) e ainda estar na tela.
    const t = barraTopo && barraTopo.getClientRects().length ? barraTopo.getBoundingClientRect() : null;
    if (t && t.height > 0) topo = Math.max(topo, t.bottom + MARGIN);
    const barraBaixo = mundo.querySelector<HTMLElement>('.skillbar');
    const b = barraBaixo && barraBaixo.getClientRects().length ? barraBaixo.getBoundingClientRect() : null;
    const xTela = pai.left + x;
    const cruza = b && b.height > 0 && xTela < b.right && xTela + w > b.left;
    if (b && cruza && b.top - MARGIN - topo >= h) base = b.top - MARGIN;
  }
  return {
    minX: esq - pai.left,
    maxX: Math.max(esq, dir - w) - pai.left,
    minY: topo - pai.top,
    maxY: Math.max(topo, base - h) - pai.top
  };
}

/**
 * O título cabe numa linha: com nome grande (o perfil de "[ADMIN] Fulano de Tal"), primeiro o espaço entre
 * as letras aperta, depois a fonte diminui até 70% do tamanho do tema; se ainda não couber, corta com
 * reticências (o CSS) e o nome inteiro fica no `title`.
 */
function useTituloQueCabe(titulo: string, ativo: boolean) {
  const ref = useRef<HTMLHeadingElement>(null);
  useLayoutEffect(() => {
    const h = ref.current;
    if (!h || !ativo) return;
    const ajustar = () => {
      h.style.fontSize = '';
      h.style.letterSpacing = '';
      const cabe = () => h.scrollWidth <= h.clientWidth + 1;
      if (cabe()) return;
      h.style.letterSpacing = '.08em';
      const base = parseFloat(getComputedStyle(h).fontSize) || 16;
      let tam = base;
      while (!cabe() && tam > base * 0.7) {
        tam -= 0.5;
        h.style.fontSize = `${tam}px`;
      }
    };
    ajustar();
    const linha = h.parentElement;
    if (!linha || typeof ResizeObserver === 'undefined') return;
    let largura = linha.clientWidth;
    const ro = new ResizeObserver(() => {
      if (linha.clientWidth === largura) return;
      largura = linha.clientWidth;
      ajustar();
    });
    ro.observe(linha);
    return () => ro.disconnect();
  }, [titulo, ativo]);
  return ref;
}

function limparPosicao(el: HTMLElement) {
  el.style.left = '';
  el.style.top = '';
  el.style.right = '';
  el.style.bottom = '';
  el.style.transform = '';
  el.style.margin = '';
}

export function GumpWindow({
  title, anchor, wide, className, snapAnchor = false, placeOnShow = false, seguirAncora = false, center = false, shown, resizable = false, bare = false, dragAnywhere = false, comeSlots = false, fechaNoEsc = true, headLead, headExtra, onMinimize, dialogo = false, focoInicial, corpoClasse, hud = false, tituloExtra, posicao, onClose, children
}: GumpWindowProps) {
  const ref = useRef<HTMLElement>(null);
  const grip = useRef<{ dx: number; dy: number } | null>(null);
  const size = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const placed = useRef(false);
  const moveu = useRef(false);
  const [dragging, setDragging] = useState(false);
  const modo = useModo();
  const compacto = useCompacto();
  useFechar(onClose ?? (() => {}), { ativo: !!onClose && fechaNoEsc && shown !== false, comeSlots });

  // Abriu: vai à frente das outras janelas. Fechou ou saiu: larga o lugar na pilha.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (shown === false) { largarDaPilha(el); return; }
    trazerParaFrente(el);
    return () => largarDaPilha(el);
  }, [shown]);
  usePrenderFoco(ref, dialogo && shown !== false, focoInicial);

  const clampIntoView = useCallback((x: number, y: number) => {
    const el = ref.current;
    if (!el) return { x, y };
    const lx = limitesDaArea(el, x);
    const cx = Math.min(Math.max(x, lx.minX), lx.maxX);
    // A barra de baixo depende de onde a janela fica na horizontal: mede de novo com o x final.
    const l = limitesDaArea(el, cx);
    return {
      x: cx,
      y: Math.min(Math.max(y, l.minY), l.maxY)
    };
  }, []);
  const tituloRef = useTituloQueCabe(title, !bare && shown !== false);

  const placeAt = useCallback((x: number, y: number) => {
    const el = ref.current;
    if (!el) return;
    const at = clampIntoView(x, y);
    el.style.left = `${at.x}px`;
    el.style.top = `${at.y}px`;
  }, [clampIntoView]);

  /*
    A âncora vira CSS direto — `right`/`bottom` quando é esse o canto — em vez
    de virar left/top calculado a partir de offsetWidth. Medir na montagem dava
    posição errada sempre que o conteúdo crescia depois (o diário enche quando
    o mundo começa a narrar), e a janela nascia meio fora da tela.
    O arrasto converte para left/top; daí em diante é o jogador quem manda.
  */
  useLayoutEffect(() => {
    if (placeOnShow) return;
    const el = ref.current;
    if (!el) return;
    if (center) limparPosicao(el);
    else applyAnchor(el, anchor);
    // Só na montagem: reposicionar a cada render desfaria o arrasto do jogador.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (!seguirAncora || shown === false) return;
    const el = ref.current;
    if (!el) return;
    applyAnchor(el, anchor);
    const at = clampIntoView(el.offsetLeft, el.offsetTop);
    el.style.left = `${at.x}px`;
    el.style.top = `${at.y}px`;
  }, [seguirAncora, shown, anchor, clampIntoView]);

  useLayoutEffect(() => {
    if (!placeOnShow || seguirAncora || placed.current || shown === false) return;
    const el = ref.current;
    if (!el) return;
    if (center) {
      limparPosicao(el);
    } else {
      const temAncora = anchor.left !== undefined || anchor.right !== undefined
        || anchor.top !== undefined || anchor.bottom !== undefined;
      /* Mundo ainda não mediu o sprite: espera o próximo render, senão trava
         a mochila no canto e nunca mais segue o jogador na 1ª abertura. */
      if (!temAncora) return;
      applyAnchor(el, anchor);
    }
    const at = clampIntoView(el.offsetLeft, el.offsetTop);
    el.style.left = `${at.x}px`;
    el.style.top = `${at.y}px`;
    placed.current = true;
  }, [placeOnShow, shown, anchor, center, clampIntoView]);

  const prevShown = useRef(shown);
  useLayoutEffect(() => {
    if (!snapAnchor) return;
    const abriu = shown !== false && prevShown.current === false;
    prevShown.current = shown;
    if (!abriu) return;
    const el = ref.current;
    if (!el) return;
    if (center) limparPosicao(el);
    else applyAnchor(el, anchor);
  }, [snapAnchor, shown, anchor, center]);

  const modoPrev = useRef(modo);
  useLayoutEffect(() => {
    if (modoPrev.current === modo) return;
    modoPrev.current = modo;
    const el = ref.current;
    if (!el) return;
    limparPosicao(el);
    el.style.width = '';
    el.style.height = '';
    if (center) limparPosicao(el);
    else applyAnchor(el, anchor);
    placed.current = false;
    // Só quando o modo troca: na montagem a âncora já foi aplicada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo]);

  /* A posição que o jogador deixou: vale por cima da âncora, toda vez que a janela abre. */
  const chaveJanela = posicao === false || dialogo || seguirAncora ? ''
    : posicao ?? className?.match(/(?:^|\s)win--([a-z0-9-]+)/)?.[1] ?? '';
  useLayoutEffect(() => {
    if (!chaveJanela || compacto || shown === false) return;
    const el = ref.current;
    const p = lerJanela(modo, chaveJanela);
    if (!el || !p) return;
    el.style.transform = 'none';
    el.style.margin = '0';
    el.style.right = 'auto';
    el.style.bottom = 'auto';
    const at = clampIntoView(p.x, p.y);
    el.style.left = `${at.x}px`;
    el.style.top = `${at.y}px`;
    placed.current = true;
  }, [chaveJanela, compacto, shown, modo, clampIntoView]);

  /* Configurações › Restaurar posições: volta à âncora de nascença. */
  useEffect(() => {
    if (!chaveJanela) return;
    return aoRestaurar(() => {
      const el = ref.current;
      if (!el) return;
      limparPosicao(el);
      if (center) return;
      applyAnchor(el, anchor);
      placed.current = false;
    });
  }, [chaveJanela, center, anchor]);

  const freezeBox = useCallback(() => {
    const el = ref.current;
    if (!el) return null;
    const box = el.getBoundingClientRect();
    // `auto` precisa ganhar do CSS (bottom/transform do grimório); string vazia não sobrescreve a folha.
    el.style.transform = 'none';
    el.style.margin = '0';
    el.style.right = 'auto';
    el.style.bottom = 'auto';
    el.style.left = `${box.left}px`;
    el.style.top = `${box.top}px`;
    return box;
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const el = ref.current;
    if (!el || e.button !== 0) return;
    if ((e.target as Element | null)?.closest?.('[data-nodrag]')) return;
    freezeBox();
    const box = el.getBoundingClientRect();
    grip.current = { dx: e.clientX - box.left, dy: e.clientY - box.top };
    moveu.current = false;
    setDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [freezeBox]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const held = grip.current;
    if (!held) return;
    placeAt(e.clientX - held.dx, e.clientY - held.dy);
    moveu.current = true;
    cursorArraste('janela', true);
  }, [placeAt]);

  const endDrag = useCallback(() => {
    const el = ref.current;
    if (grip.current && moveu.current && el && chaveJanela) gravarJanela(modo, chaveJanela, { x: el.offsetLeft, y: el.offsetTop });
    moveu.current = false;
    grip.current = null;
    setDragging(false);
    cursorArraste('janela', false);
  }, [chaveJanela, modo]);

  const onResizeDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    const box = freezeBox();
    const el = ref.current;
    if (!box || !el) return;
    el.style.width = `${box.width}px`;
    el.style.height = `${box.height}px`;
    size.current = { x: e.clientX, y: e.clientY, w: box.width, h: box.height };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [freezeBox]);

  const onResizeMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const held = size.current;
    const el = ref.current;
    if (!held || !el) return;
    const maxW = Math.max(MIN_W, window.innerWidth - MARGIN * 2);
    const maxH = Math.max(MIN_H, window.innerHeight - MARGIN * 2);
    const w = Math.min(Math.max(held.w + (e.clientX - held.x), MIN_W), maxW);
    const h = Math.min(Math.max(held.h + (e.clientY - held.y), MIN_H), maxH);
    el.style.width = `${w}px`;
    el.style.height = `${h}px`;
    placeAt(el.offsetLeft, el.offsetTop);
  }, [placeAt]);

  const onResizeUp = useCallback(() => { size.current = null; }, []);

  // A janela não pode ficar fora da tela quando o navegador encolhe.
  // Só vale para quem já foi arrastada: as ancoradas o CSS já reposiciona.
  useEffect(() => {
    const onResize = () => {
      const el = ref.current;
      if (el && el.style.left && el.style.top) placeAt(el.offsetLeft, el.offsetTop);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [placeAt]);

  const dragBind = compacto ? {} : {
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag
  };

  return (
    <section
      ref={ref}
      aria-label={title}
      role={dialogo ? 'dialog' : undefined}
      aria-modal={dialogo || undefined}
      hidden={shown === false}
      onPointerDownCapture={() => { if (ref.current) trazerParaFrente(ref.current); }}
      className={`win gump${wide ? ' win--wide' : ''}${hud ? ' win--hud' : ''}${resizable ? ' is-resizable' : ''}${bare ? ' win--bare' : ''}${center ? ' win--center' : ''}${onMinimize && !bare ? ' tem-min' : ''}${onMinimize && onClose && !bare ? ' tem-min-fechar' : ''}${dragging ? ' is-dragging' : ''}${className ? ` ${className}` : ''}`}
      {...(bare || dragAnywhere ? dragBind : {})}
    >
      {bare ? (
        <>
          <h2 className="sr">{title}</h2>
          {onClose && (
            <button
              type="button"
              className="win__close win__close--bare"
              onClick={onClose}
              onPointerDown={e => e.stopPropagation()}
              aria-label={`Fechar ${title}`}
            >
              ✕
            </button>
          )}
        </>
      ) : (
        <header className="gump__head" {...dragBind}>
          <div className="gump__title-row">
            {headLead}
            <h2 ref={tituloRef} className="gump__title" title={title}>{title}</h2>
            {tituloExtra}
          </div>
          {headExtra}
          {onMinimize && (
            <button
              type="button"
              className="win__min"
              onClick={onMinimize}
              onPointerDown={e => e.stopPropagation()}
              aria-label={t('janela.minimizar', { titulo: title })}
              title={t('janela.minimizar', { titulo: title })}
            >
              <i aria-hidden="true" />
            </button>
          )}
          {onClose && (
            <button type="button" className="win__close" onClick={onClose} aria-label={`Fechar ${title}`}>
              ✕
            </button>
          )}
        </header>
      )}
      <div className={`gump__body${corpoClasse ? ` ${corpoClasse}` : ''}`}>{children}</div>
      {resizable && !compacto && (
        <button
          type="button"
          className="win__resize"
          aria-label={`Redimensionar ${title}`}
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onPointerCancel={onResizeUp}
        />
      )}
    </section>
  );
}
